import { parse, stringify } from 'flatted';
import { createTransform } from 'redux-persist';
import { ConversationFilterEnum } from '~/enums/conversations';
import { AccountInfoState } from '~/types/accountInfo';
import { initialState as initialConversationState } from './reducers/conversation';
import { initialState as initialAccountState } from './reducers/accountInfo';

// Define a minimal persisted shape
type PersistedAccountInfo = Pick<AccountInfoState, 'tourCompleted'>;

export const accountInfoTransform = createTransform<
  AccountInfoState, // inbound state (from Redux)
  PersistedAccountInfo // outbound state (to storage)
>(
  inbound => ({
    tourCompleted: inbound.tourCompleted,
  }),
  outbound => ({
    ...initialAccountState,
    tourCompleted: outbound?.tourCompleted,
  }),
  { whitelist: ['accountInfo'] }
);

/**
 * `system.deviceManager` holds the RESOLVED audio devices - which microphone and speaker are
 * actually in use. That is derived from the hardware attached right now, so it must never
 * outlive the session that computed it: a headset unplugged while the app was closed would come
 * back as "in use", agreeing with the pin that selected it, so the first call after a restart would
 * ask for a deviceId that no longer exists - and, because the constraint is advisory, would get
 * some other microphone while the picker and the SIP header both still named the headset.
 *
 * Only the user's intent is persisted (`userPreferences`, `ringDevice`). The resolution is
 * recomputed by the phoneDeviceSetup thunk on startup, against the hardware actually present.
 *
 * Applied on both legs: inbound keeps stale devices out of IndexedDB, outbound also neutralizes
 * payloads written by earlier builds. Kept as `{ audio: {} }` rather than undefined because
 * consumers read `deviceManager.audio?.speaker` - the `.audio` step is not optional.
 */
const withoutResolvedDevices = (state: any) => ({
  ...state,
  deviceManager: { audio: {} },
});

export const systemTransform = createTransform(
  // Before persist (inbound)
  (inboundState: any) =>
    stringify(
      withoutResolvedDevices({
        ...inboundState,
        showUpdaterMessage: true,
        temporaryCountryCode: null,
        voiceSettingsOpen: false,
      })
    ),

  // Before rehydrate (outbound)
  outboundState => {
    const outbound = parse(outboundState);
    return withoutResolvedDevices({
      ...outbound,
      showUpdaterMessage: true,
      temporaryCountryCode: null,
      // Transient UI state. Persisting it would spring the Voice Settings dialog open on the
      // user's next call after a restart.
      voiceSettingsOpen: false,
      status: outbound.status === 'Available' ? 'Unavailable' : outbound.status,
    });
  },

  // Only apply to 'system' reducer
  { whitelist: ['system'] }
);

const checkConversationFilters = (outbound: any) => {
  const { type } = outbound.filters;

  if (type !== ConversationFilterEnum.MY_MESSAGES) {
    return { conversations: [] };
  }

  return {};
};

export const conversationTransform = createTransform(
  // Before persist (inbound)
  inboundState => stringify(inboundState),

  // Before rehydrate (outbound)
  outboundState => {
    const outbound = parse(outboundState);
    return {
      ...outbound,
      hiddenBottomNav: false,
      openSelection: false,
      lastReadIndex: 0,
      filters: initialConversationState.filters,
      ...checkConversationFilters(outbound),
    };
  },

  // Only apply to 'conversation' reducer
  { whitelist: ['conversation'] }
);
